/**
 * チバリヨ - S4 タスク一覧 (Phase 2-B-3 / 他メンバー閲覧対応)
 *
 * 仕様:
 *   - 上部フィルタで「自分」/「全員」を切替。全員時はメンバー別チップで絞り込み可
 *   - 全員時、自分以外のタスクには担当者バッジ(アバター+名前)を表示
 *   - 自分以外のタスクは閲覧専用 (完了/編集/スワイプ不可)。admin は全員操作可
 *   - グループ: 期限超過 / 本日 / 明日以降 / 期限なし / 完了済み(直近7日・折りたたみ)
 *   - 並び: 各グループ内で期限日昇順 → 同日は作成日時昇順
 *   - 操作 (操作可能なタスクのみ):
 *       右スワイプ or ○ タップ = 完了
 *       左スワイプ or カードタップ = 編集 (削除は編集画面内)
 *       完了済みの ✓ タップ = 未完了に戻す
 *
 * クエリ設計 (v12 教訓: orderBy はフィールド欠落 doc を除外する):
 *   - 自分モード
 *       未完了: where memberId == 自分 AND status == 'open' (等価のみ・orderBy なし)
 *               → dueDate: null の doc も漏れずに取得し、クライアント側でソート
 *       完了済み: where memberId == 自分 AND status == 'done'
 *                 AND completedAt >= 7日前, orderBy completedAt desc
 *               → 複合インデックス (memberId, status, completedAt DESC) 必須
 *   - 全員モード (メンバー別の絞り込みはクライアント側で実施)
 *       未完了: where status == 'open' (単一等価・複合インデックス不要)
 *       完了済み: where status == 'done' AND completedAt >= 7日前, orderBy completedAt desc
 *               → 複合インデックス (status, completedAt DESC) 必須
 *
 *   ルール上 tasks の read は isActiveUser() なので他人のタスク取得は許可される。
 *   一方 update/delete は (sales && 自分) or admin のみ → 他人のカードは UI で操作を塞ぐ。
 *
 * 使い方:
 *   const ctrl = mountS4Tasks(rootEl, { currentMember, showToast, onEditTask });
 *   ctrl.onShow({ reload });  // タブ再表示時
 *   ctrl.destroy();
 */

import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  deleteField,
  serverTimestamp,
  Timestamp,
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';
import { db } from './firebase-init.js';
import { listMembers } from './auth.js';

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
const DONE_DAYS = 7; // 完了済みの表示範囲(日)
// 期限が今日からこの日数より先の未完了タスクは一覧に出さない。
// (P7 ダッシュボードの「アクション備忘録」側で扱う。期限が近づき30日以内になると自動で一覧に出る)
const FUTURE_HIDE_DAYS = 30;

// アバター色 (main.js の FALLBACK_AVATAR_COLORS と同一定義。担当者バッジ用)
const AVATAR_COLORS = {
  blue:   { text: '#0C447C', bg: '#E6F1FB' },
  teal:   { text: '#0F6E56', bg: '#E1F5EE' },
  amber:  { text: '#854F0B', bg: '#FAEEDA' },
  purple: { text: '#3C3489', bg: '#EEEDFE' },
  coral:  { text: '#A32D2D', bg: '#FCEBEB' },
  slate:  { text: '#475569', bg: '#F1F5F9' },
  rose:   { text: '#9F1239', bg: '#FFE4E6' },
  indigo: { text: '#3730A3', bg: '#E0E7FF' },
};

function todayStr() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** 今日から n 日後の "YYYY-MM-DD" */
function daysAheadStr(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** "YYYY-MM-DD" → "6/15(月)" */
function formatDue(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return `${m}/${d}(${WEEKDAYS[dt.getDay()]})`;
}

/** 期限超過日数 (dueDate < today 前提) */
function overdueDays(dueDate, today) {
  const [y1, m1, d1] = dueDate.split('-').map(Number);
  const [y2, m2, d2] = today.split('-').map(Number);
  const ms = new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1);
  return Math.round(ms / 86400000);
}

function tsMillis(ts) {
  return ts && typeof ts.toMillis === 'function' ? ts.toMillis() : 0;
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// =====================================================================
// 公開関数
// =====================================================================
export function mountS4Tasks(rootEl, opts) {
  const { currentMember, showToast, onEditTask } = opts || {};
  const me = currentMember.memberId;
  const isAdmin = currentMember.role === 'admin';

  const state = {
    openTasks: [],   // status == 'open'
    doneTasks: [],   // status == 'done' (直近7日)
    loading: false,
    doneExpanded: false,
    busy: false,     // 完了/戻す の連打防止
    destroyed: false,
    scope: 'me',          // 'me' | 'all'
    selectedMember: '',   // 全員モードでのメンバー別絞り込み ('' = 全員)
  };

  // memberId -> member (担当者バッジの名前/色用)。listMembers() で非同期に埋める
  const membersById = { [me]: currentMember };
  let members = []; // active メンバー (チップ生成用)

  rootEl.innerHTML = `
    <div class="s4t-root">
      <div class="s4t-page-header">
        <h2 class="s4t-page-title">タスク</h2>
        <button type="button" class="s4t-refresh" id="s4t-refresh" aria-label="再読み込み">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6"/>
          </svg>
        </button>
      </div>
      <div class="s4t-filter">
        <div class="s4t-scope" id="s4t-scope">
          <button type="button" class="s4t-scope-btn is-active" data-scope="me">自分</button>
          <button type="button" class="s4t-scope-btn" data-scope="all">全員</button>
        </div>
        <div class="s4t-members" id="s4t-members" hidden></div>
      </div>
      <div class="s4t-error" id="s4t-error" hidden></div>
      <div class="s4t-list" id="s4t-list">
        <div class="s4t-loading">読み込み中…</div>
      </div>
    </div>
  `;

  const listEl = rootEl.querySelector('#s4t-list');
  const errorEl = rootEl.querySelector('#s4t-error');
  const scopeEl = rootEl.querySelector('#s4t-scope');
  const membersEl = rootEl.querySelector('#s4t-members');
  rootEl.querySelector('#s4t-refresh').addEventListener('click', () => reload());

  // -------------------------------------------------------------------
  // フィルタ (自分/全員 + メンバー別チップ)
  // -------------------------------------------------------------------
  scopeEl.querySelectorAll('.s4t-scope-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const next = btn.dataset.scope;
      if (next === state.scope) return;
      state.scope = next;
      state.selectedMember = ''; // 切替時は絞り込みをリセット
      scopeEl.querySelectorAll('.s4t-scope-btn').forEach((b) => {
        b.classList.toggle('is-active', b.dataset.scope === next);
      });
      membersEl.hidden = next !== 'all';
      renderMemberChips(); // selectedMember リセットを反映 (「全員」を active に)
      reload();
    });
  });

  /** active メンバーを読み込み、全員モードのメンバー別チップを描画 */
  async function loadMembers() {
    try {
      const list = await listMembers();
      for (const m of list) membersById[m.memberId] = m;
      members = list
        // 閲覧者(viewer)はタスクを持たないためチップにも出さない
        .filter((m) => m.active !== false && m.chibariyoAccess !== false && m.role !== 'viewer')
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ja'));
      renderMemberChips();
    } catch (e) {
      console.warn('[S4] メンバー一覧の取得に失敗 (担当者名は memberName を使用):', e);
    }
  }

  function renderMemberChips() {
    const chips = [`<button type="button" class="s4t-chip ${state.selectedMember === '' ? 'is-active' : ''}" data-member="">全員</button>`];
    for (const m of members) {
      const label = m.memberId === me ? `${m.name || '自分'}(自分)` : (m.name || m.memberId);
      chips.push(`<button type="button" class="s4t-chip ${state.selectedMember === m.memberId ? 'is-active' : ''}" data-member="${escapeHtml(m.memberId)}">${escapeHtml(label)}</button>`);
    }
    membersEl.innerHTML = chips.join('');
    membersEl.querySelectorAll('.s4t-chip').forEach((chip) => {
      chip.onclick = () => {
        state.selectedMember = chip.dataset.member;
        membersEl.querySelectorAll('.s4t-chip').forEach((c) => {
          c.classList.toggle('is-active', c.dataset.member === state.selectedMember);
        });
        render(); // 絞り込みはクライアント側 → 再取得不要
      };
    });
  }

  loadMembers();

  // -------------------------------------------------------------------
  // データ取得
  // -------------------------------------------------------------------
  async function fetchTasks() {
    const allScope = state.scope === 'all';
    const cutoff = Timestamp.fromMillis(Date.now() - DONE_DAYS * 86400000);

    // 未完了: 等価条件のみ (orderBy を使わない → dueDate:null も漏れない)
    //   自分モード: (memberId, status) / 全員モード: status のみ (複合インデックス不要)
    const openQ = allScope
      ? query(
          collection(db, 'tasks'),
          where('status', '==', 'open')
        )
      : query(
          collection(db, 'tasks'),
          where('memberId', '==', me),
          where('status', '==', 'open')
        );

    // 完了済み (直近7日):
    //   自分モード: 複合インデックス (memberId, status, completedAt DESC)
    //   全員モード: 複合インデックス (status, completedAt DESC)
    const doneQ = allScope
      ? query(
          collection(db, 'tasks'),
          where('status', '==', 'done'),
          where('completedAt', '>=', cutoff),
          orderBy('completedAt', 'desc')
        )
      : query(
          collection(db, 'tasks'),
          where('memberId', '==', me),
          where('status', '==', 'done'),
          where('completedAt', '>=', cutoff),
          orderBy('completedAt', 'desc')
        );

    const [openSnap, doneSnap] = await Promise.all([getDocs(openQ), getDocs(doneQ)]);

    // 30日より先の未完了タスクは一覧に出さない (P7 の「アクション備忘録」で扱う。
    // 期限なし・期限超過・30日以内は従来どおり表示)。データ自体は消さない。
    const horizon = daysAheadStr(FUTURE_HIDE_DAYS);
    state.openTasks = openSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((t) => !(t.dueDate && t.dueDate > horizon));
    state.doneTasks = doneSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // クライアント側ソート: 期限日昇順 → 同日(/期限なし同士)は作成日時昇順
    state.openTasks.sort((a, b) => {
      const da = a.dueDate || '9999-99-99'; // 期限なしは末尾 (表示はグループで分離)
      const dbb = b.dueDate || '9999-99-99';
      if (da !== dbb) return da < dbb ? -1 : 1;
      return tsMillis(a.createdAt) - tsMillis(b.createdAt);
    });
  }

  async function reload() {
    if (state.loading || state.destroyed) return;
    state.loading = true;
    errorEl.hidden = true;
    try {
      await fetchTasks();
      if (state.destroyed) return;
      render();
    } catch (e) {
      console.error('[S4] タスク取得失敗:', e);
      errorEl.textContent = 'タスクの読み込みに失敗しました: ' + (e.message || e);
      errorEl.hidden = false;
      listEl.innerHTML = '';
    } finally {
      state.loading = false;
    }
  }

  // -------------------------------------------------------------------
  // 描画
  // -------------------------------------------------------------------
  /** メンバー別チップによるクライアント側絞り込み (全員モードのみ有効) */
  function applyMemberFilter(list) {
    if (state.scope === 'all' && state.selectedMember) {
      return list.filter((t) => t.memberId === state.selectedMember);
    }
    return list;
  }

  /** このタスクを操作 (完了/編集/削除) できるか: 自分の担当 or admin */
  function canMutate(t) {
    return t.memberId === me || isAdmin;
  }

  /** 担当者バッジ (全員モードで自分以外のタスクのみ表示) */
  function assigneeBadge(t) {
    const m = membersById[t.memberId];
    const name = (m && m.name) || t.memberName || '?';
    const color = AVATAR_COLORS[m && m.avatarColor] || AVATAR_COLORS.slate;
    return `<span class="s4t-assignee"><span class="s4t-assignee-av" style="color:${color.text};background:${color.bg}">${escapeHtml(name.slice(0, 1))}</span>${escapeHtml(name)}</span>`;
  }

  function render() {
    const today = todayStr();

    const openTasks = applyMemberFilter(state.openTasks);
    const doneTasks = applyMemberFilter(state.doneTasks);

    const groups = {
      overdue: [], today: [], upcoming: [], nodue: [],
    };
    openTasks.forEach((t) => {
      if (!t.dueDate) groups.nodue.push(t);
      else if (t.dueDate < today) groups.overdue.push(t);
      else if (t.dueDate === today) groups.today.push(t);
      else groups.upcoming.push(t);
    });

    const totalOpen = openTasks.length;

    let html = '';

    if (totalOpen === 0) {
      html += `
        <div class="s4t-empty">
          <div class="s4t-empty-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>
          </div>
          <div class="s4t-empty-text">未完了のタスクはありません</div>
          <div class="s4t-empty-sub">下の + ボタンからタスクを作成できます</div>
        </div>
      `;
    } else {
      html += renderGroup('overdue', '期限超過', groups.overdue, today);
      html += renderGroup('today', '本日', groups.today, today);
      html += renderGroup('upcoming', '明日以降', groups.upcoming, today);
      html += renderGroup('nodue', '期限なし', groups.nodue, today);
    }

    // 完了済み (折りたたみ)
    if (doneTasks.length > 0) {
      html += `
        <div class="s4t-done-section">
          <button type="button" class="s4t-done-toggle" id="s4t-done-toggle">
            <span class="s4t-done-toggle-label">完了済み (直近${DONE_DAYS}日)</span>
            <span class="s4t-done-count">${doneTasks.length}</span>
            <svg class="s4t-done-chevron ${state.doneExpanded ? 'is-open' : ''}" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>
          <div class="s4t-done-list" id="s4t-done-list" ${state.doneExpanded ? '' : 'hidden'}>
            ${doneTasks.map((t) => renderDoneCard(t)).join('')}
          </div>
        </div>
      `;
    }

    listEl.innerHTML = html;
    bindCardEvents(today);
  }

  function renderGroup(key, label, tasks, today) {
    if (tasks.length === 0) return '';
    return `
      <div class="s4t-group s4t-group-${key}">
        <div class="s4t-group-header">
          <span class="s4t-group-label">${label}</span>
          <span class="s4t-group-count">${tasks.length}</span>
        </div>
        ${tasks.map((t) => renderCard(t, key, today)).join('')}
      </div>
    `;
  }

  function renderCard(t, groupKey, today) {
    const mutable = canMutate(t);
    const showBadge = state.scope === 'all' && t.memberId !== me;

    const metaParts = [];
    if (showBadge) metaParts.push(assigneeBadge(t));
    if (t.dueDate) {
      if (groupKey === 'overdue') {
        metaParts.push(`<span class="s4t-due s4t-due-overdue">${formatDue(t.dueDate)} ・ ${overdueDays(t.dueDate, today)}日超過</span>`);
      } else {
        metaParts.push(`<span class="s4t-due">${formatDue(t.dueDate)}</span>`);
      }
    }
    if (t.client && t.client.clientText) {
      metaParts.push(`<span class="s4t-client">${escapeHtml(t.client.clientText)}</span>`);
    }

    // 操作可能なら完了ボタン+編集タップ。閲覧専用なら静的なチェック印・タップ無効。
    const checkHtml = mutable
      ? `<button type="button" class="s4t-check" data-action="complete" aria-label="完了にする"></button>`
      : `<span class="s4t-check s4t-check-readonly" aria-hidden="true"></span>`;
    const bodyAttr = mutable ? ' data-action="edit"' : '';

    return `
      <div class="s4t-card-wrap${mutable ? '' : ' is-readonly'}" data-task-id="${t.id}" data-mutable="${mutable ? '1' : '0'}">
        <div class="s4t-action s4t-action-complete" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M5 12l5 5L20 7"/></svg>
          <span>完了</span>
        </div>
        <div class="s4t-action s4t-action-edit" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M17 3l4 4L8 20l-5 1 1-5L17 3z"/></svg>
          <span>編集</span>
        </div>
        <div class="s4t-card">
          ${checkHtml}
          <div class="s4t-body"${bodyAttr}>
            <div class="s4t-title">${escapeHtml(t.title)}</div>
            ${metaParts.length ? `<div class="s4t-meta">${metaParts.join('<span class="s4t-meta-sep">・</span>')}</div>` : ''}
            ${t.note ? `<div class="s4t-note">${escapeHtml(t.note)}</div>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  function renderDoneCard(t) {
    const mutable = canMutate(t);
    const showBadge = state.scope === 'all' && t.memberId !== me;
    const doneAt = t.completedAt && typeof t.completedAt.toDate === 'function'
      ? (() => { const d = t.completedAt.toDate(); return `${d.getMonth() + 1}/${d.getDate()} 完了`; })()
      : '';

    // 操作可能なら ✓ タップで未完了に戻せる。閲覧専用は静的なチェック印。
    const checkHtml = mutable
      ? `<button type="button" class="s4t-check is-checked" data-action="revert" aria-label="未完了に戻す"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg></button>`
      : `<span class="s4t-check is-checked s4t-check-readonly" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 12l5 5L20 7"/></svg></span>`;

    const metaParts = [];
    if (showBadge) metaParts.push(assigneeBadge(t));
    if (doneAt) metaParts.push(`<span class="s4t-done-at">${doneAt}</span>`);
    if (t.client && t.client.clientText) metaParts.push(`<span class="s4t-client">${escapeHtml(t.client.clientText)}</span>`);

    return `
      <div class="s4t-card-wrap is-done" data-task-id="${t.id}" data-mutable="${mutable ? '1' : '0'}">
        <div class="s4t-card">
          ${checkHtml}
          <div class="s4t-body">
            <div class="s4t-title">${escapeHtml(t.title)}</div>
            ${metaParts.length ? `<div class="s4t-meta">${metaParts.join('<span class="s4t-meta-sep">・</span>')}</div>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  // -------------------------------------------------------------------
  // イベント (タップ + スワイプ)
  // -------------------------------------------------------------------
  function bindCardEvents() {
    // 完了済みトグル
    const toggle = listEl.querySelector('#s4t-done-toggle');
    if (toggle) {
      toggle.addEventListener('click', () => {
        state.doneExpanded = !state.doneExpanded;
        const dl = listEl.querySelector('#s4t-done-list');
        const ch = toggle.querySelector('.s4t-done-chevron');
        if (dl) dl.hidden = !state.doneExpanded;
        if (ch) ch.classList.toggle('is-open', state.doneExpanded);
      });
    }

    listEl.querySelectorAll('.s4t-card-wrap').forEach((wrap) => {
      const id = wrap.dataset.taskId;
      const card = wrap.querySelector('.s4t-card');
      const isDone = wrap.classList.contains('is-done');
      const mutable = wrap.dataset.mutable === '1';
      let suppressClick = false;

      // タップ: ○=完了 / ✓=戻す / 本文=編集 (閲覧専用は data-action 無しで無反応)
      wrap.addEventListener('click', (e) => {
        if (suppressClick) { suppressClick = false; return; }
        const actionEl = e.target.closest('[data-action]');
        if (!actionEl) return;
        const action = actionEl.dataset.action;
        if (action === 'complete') completeTask(id);
        else if (action === 'revert') revertTask(id);
        else if (action === 'edit') editTask(id);
      });

      // スワイプ (操作可能な未完了カードのみ)
      if (!isDone && mutable) {
        attachSwipe(wrap, card, {
          onCommit: (dir) => {
            suppressClick = true;
            if (dir === 'right') completeTask(id);
            else editTask(id);
          },
          onMoved: () => { suppressClick = true; },
        });
      }
    });
  }

  function attachSwipe(wrapEl, cardEl, { onCommit, onMoved }) {
    let startX = 0, startY = 0, dx = 0;
    let active = false;
    let horizontal = null; // null=未判定

    cardEl.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      dx = 0;
      active = true;
      horizontal = null;
      cardEl.style.transition = 'none';
    }, { passive: true });

    cardEl.addEventListener('touchmove', (e) => {
      if (!active) return;
      const t = e.touches[0];
      const mx = t.clientX - startX;
      const my = t.clientY - startY;
      if (horizontal === null && (Math.abs(mx) > 8 || Math.abs(my) > 8)) {
        horizontal = Math.abs(mx) > Math.abs(my);
      }
      if (!horizontal) return;
      dx = Math.max(-96, Math.min(96, mx));
      cardEl.style.transform = `translateX(${dx}px)`;
      wrapEl.classList.toggle('is-swiping-right', dx > 12);
      wrapEl.classList.toggle('is-swiping-left', dx < -12);
    }, { passive: true });

    function end() {
      if (!active) return;
      active = false;
      cardEl.style.transition = '';
      const commit = horizontal && Math.abs(dx) >= 60;
      const dir = dx > 0 ? 'right' : 'left';
      cardEl.style.transform = '';
      wrapEl.classList.remove('is-swiping-right', 'is-swiping-left');
      if (horizontal && Math.abs(dx) > 10 && onMoved) onMoved();
      if (commit) onCommit(dir);
      dx = 0;
    }
    cardEl.addEventListener('touchend', end);
    cardEl.addEventListener('touchcancel', end);
  }

  // -------------------------------------------------------------------
  // 操作
  // -------------------------------------------------------------------
  async function completeTask(taskId) {
    if (state.busy) return;
    state.busy = true;
    try {
      await updateDoc(doc(db, 'tasks', taskId), {
        status: 'done',
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      if (showToast) showToast('タスクを完了しました', 'success');
      await reloadAfterMutation();
    } catch (e) {
      console.error('[S4] 完了処理失敗:', e);
      if (showToast) showToast('完了処理に失敗しました', 'error');
    } finally {
      state.busy = false;
    }
  }

  async function revertTask(taskId) {
    if (state.busy) return;
    state.busy = true;
    try {
      await updateDoc(doc(db, 'tasks', taskId), {
        status: 'open',
        completedAt: deleteField(),
        updatedAt: serverTimestamp(),
      });
      if (showToast) showToast('未完了に戻しました', 'success');
      await reloadAfterMutation();
    } catch (e) {
      console.error('[S4] 戻す処理失敗:', e);
      if (showToast) showToast('処理に失敗しました', 'error');
    } finally {
      state.busy = false;
    }
  }

  async function reloadAfterMutation() {
    // busy フラグを保持したまま再取得 (reload() は loading ガードのみ)
    try {
      await fetchTasks();
      if (!state.destroyed) render();
    } catch (e) {
      console.error('[S4] 再読み込み失敗:', e);
    }
  }

  function editTask(taskId) {
    const t = state.openTasks.find((x) => x.id === taskId)
      || state.doneTasks.find((x) => x.id === taskId);
    if (t && onEditTask) onEditTask(t);
  }

  // -------------------------------------------------------------------
  // 初回ロード + 公開 API
  // -------------------------------------------------------------------
  reload();

  return {
    onShow: (o = {}) => {
      if (o.reload) reload();
    },
    destroy: () => {
      state.destroyed = true;
    },
  };
}
